/** Product UI frames used as landing screenshots (mirror of Helpers, Chat, Connections). */

function Chrome({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/70 bg-white/70 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.35)] backdrop-blur-xl">
      <div className="flex items-center gap-2 border-b border-slate-200/70 bg-white/80 px-4 py-2.5">
        <span className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        </span>
        <p className="truncate text-xs font-medium text-slate-500">{title}</p>
      </div>
      <div className="bg-gradient-to-b from-slate-50/90 to-teal-50/40 p-4 md:p-5">
        {children}
      </div>
    </div>
  );
}

export function HelpersShot() {
  return (
    <Chrome title="primeassistant.app / automated assistants">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">Your automated assistants</p>
        <span className="rounded-lg bg-teal-700 px-2.5 py-1 text-[11px] font-semibold text-white">
          Create a helper
        </span>
      </div>
      <ul className="space-y-2">
        {[
          {
            name: "Pipeline digest",
            status: "Scheduled · 8:00 AM",
            tone: "bg-teal-100 text-teal-900",
          },
          {
            name: "Missing documents",
            status: "Ran overnight",
            tone: "bg-emerald-100 text-emerald-900",
          },
          {
            name: "Rate notes",
            status: "Next run Monday",
            tone: "bg-slate-100 text-slate-800",
          },
        ].map((row) => (
          <li
            key={row.name}
            className="flex items-center justify-between rounded-xl border border-white/80 bg-white/80 px-3 py-2.5 shadow-sm"
          >
            <div>
              <p className="text-sm font-medium text-slate-900">{row.name}</p>
              <p className="text-[11px] text-slate-500">{row.status}</p>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${row.tone}`}
            >
              Active
            </span>
          </li>
        ))}
      </ul>
    </Chrome>
  );
}

export function ChatShot() {
  return (
    <Chrome title="primeassistant.app / chat">
      <p className="mb-3 text-xs font-medium text-teal-800">Assistant · Review application</p>
      <div className="space-y-2.5 text-[13px] leading-snug">
        <div className="ml-8 rounded-2xl rounded-br-md bg-teal-700 px-3 py-2 text-white">
          Summarize the Chen deal and flag missing docs.
        </div>
        <div className="mr-6 rounded-2xl rounded-bl-md border border-white/80 bg-white/90 px-3 py-2 text-slate-700">
          Velocity shows two outstanding conditions. I attached the Drive checklist PDF.
        </div>
        <div className="ml-8 rounded-2xl rounded-br-md bg-teal-700 px-3 py-2 text-white">
          Research the lender&apos;s current turnaround time.
        </div>
        <div className="mr-6 rounded-2xl rounded-bl-md border border-white/80 bg-white/90 px-3 py-2 text-slate-700">
          Public page cites 3–5 business days — source linked in the thread.
        </div>
      </div>
      <div className="mt-3 flex gap-2 rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2">
        <span className="flex-1 text-xs text-slate-400">Message or drop a file…</span>
        <span className="rounded-lg bg-teal-700 px-2 py-0.5 text-[10px] font-semibold text-white">
          Send
        </span>
      </div>
    </Chrome>
  );
}

export function ConnectionsShot() {
  return (
    <Chrome title="primeassistant.app / settings">
      <p className="mb-3 text-sm font-semibold text-slate-900">Connections</p>
      <ul className="space-y-2">
        {[
          { name: "Velocity", detail: "Pipeline · contacts · deals", on: true },
          { name: "Gmail", detail: "Send & read threads", on: true },
          { name: "Google Drive", detail: "Application files", on: false },
          { name: "Cloud AI (Modal)", detail: "Chat & automated assistants hosted", on: true },
        ].map((c) => (
          <li
            key={c.name}
            className="flex items-center justify-between rounded-xl border border-white/80 bg-white/85 px-3 py-2.5"
          >
            <div>
              <p className="text-sm font-medium text-slate-900">{c.name}</p>
              <p className="text-[11px] text-slate-500">{c.detail}</p>
            </div>
            <span
              className={`h-2.5 w-2.5 rounded-full ${c.on ? "bg-emerald-500" : "bg-slate-300"}`}
              title={c.on ? "Connected" : "Not connected"}
            />
          </li>
        ))}
      </ul>
    </Chrome>
  );
}
